import React, { Fragment, useCallback, useMemo } from 'react';
import type { TriggerProps } from '@atlaskit/popup/types';
import Skeleton from '@atlaskit/skeleton';
import { Grid } from '@atlaskit/primitives/grid';
import Flex from '@atlaskit/primitives/components/flex';
import { xcss } from '@atlaskit/primitives/xcss';
import { token } from '@atlaskit/tokens';
import { AppSwitcher as AkAppSwitcherNav4 } from '@atlaskit/navigation-system/top-nav-items';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import { SwitchToTooltipText } from '@atlassian/switcher/switch-to-tooltip-text';
import {
	createUnitTileAppSwitcherIcon,
	type UnitProfile,
} from '@atlassian/switcher/unit-tile-app-switcher-icon';

type Props = {
	// NAVX-5411: spike measuring the cost of fetching + rendering a unit tile
	// as the app switcher trigger icon. Null when the experiment is off.
	unitProfile: UnitProfile | null;
};

/**
 * AppSwitcherPlaceholderWithLoadingPopup is designed to show as a fallback,
 * while the real AppSwitcher component is loading.
 */
export function AppSwitcherPlaceholderWithLoadingPopup({ unitProfile }: Props): React.JSX.Element {
	// NAVX-5411: keep a stable trigger reference (matching the original
	// `trigger={Trigger}`) so the Popup doesn't re-render its trigger on
	// every parent render; only recreate when the unit profile changes.
	const renderTrigger = useCallback(
		(triggerProps: TriggerProps) => <Trigger {...triggerProps} unitProfile={unitProfile} />,
		[unitProfile],
	);

	return (
		<Popup
			content={() => <SkeletonContent />}
			isOpen
			// eslint-disable-next-line jira/choreographer/message-id-by-folder-structure
			messageId="navigation-apps-topnav4-app-switcher.ui.trigger.popup.loading.popup"
			messageType="transactional"
			placement="bottom-start"
			shouldRenderToParent
			trigger={renderTrigger}
		/>
	);
}

function Trigger(triggerProps: TriggerProps & Props) {
	const { unitProfile, ...rest } = triggerProps;
	// NAVX-5411: swap in a platform-owned unit tile icon for the app switcher
	// trigger when a unit profile is available for the current site. Memoized
	// so the icon component type stays stable across renders and AkAppSwitcherNav4
	// doesn't unmount/remount it unnecessarily.
	const icon = useMemo(
		() => (unitProfile ? createUnitTileAppSwitcherIcon(unitProfile) : undefined),
		[unitProfile],
	);

	return (
		<AkAppSwitcherNav4
			{...rest}
			label={SwitchToTooltipText}
			icon={icon}
			aria-expanded={false}
			aria-haspopup
			interactionName="jira_app_switcher"
		/>
	);
}

function SkeletonContent() {
	return (
		<Grid
			xcss={containerStyles}
			rowGap="space.200"
			columnGap="space.150"
			templateColumns="32px 1fr"
		>
			<SkeletonItems />
		</Grid>
	);
}

function SkeletonItems() {
	return Array.from({ length: 5 }).map((_, index) => (
		<Fragment key={index}>
			<Skeleton width="32px" height="32px" isShimmering borderRadius={token('radius.large')} />
			<Flex justifyContent="center" direction="column">
				<Skeleton width="100%" height="10px" isShimmering />
			</Flex>
		</Fragment>
	));
}

const containerStyles = xcss({
	width: '250px',
	padding: 'space.200',
});
